import {
    addWaterEntry,
    addWeightEntry,
    addSleepEntry,
    addActivityEntry,
    addFoodEntry,
    scheduleCoachAppointment,
    scheduleDoctorAppointment,
    sendUserConversationEncrypted,
    addGlucoseEntry,
    fetchMyOrders
} from './api';

// --- Client-Side Tool Implementation ---
export const getHealthSummary = async ({ day }, healthData) => {
    console.log(`🛠️ Tool called: getHealthSummary for '${day}'`);
    if (!healthData || !healthData.data) {
        return { success: false, error: "Health data not loaded yet." };
    }
    const today = new Date();
    let targetDate;
    if (day === 'today') {
        targetDate = today.toISOString().split('T')[0];
    } else if (day === 'yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        targetDate = yesterday.toISOString().split('T')[0];
    } else {
        return { success: false, error: "Invalid day specified. Use 'today' or 'yesterday'." };
    }
    const summary = {
        date: targetDate,
        foodLogs: healthData.data.food.filter(log => log.loggedDate.startsWith(targetDate)),
        waterLogs: healthData.data.water.filter(log => log.loggedDate.startsWith(targetDate)),
        sleepLogs: healthData.data.sleep.filter(log => log.loggedDate.startsWith(targetDate)),
    };
    console.log("📊 Generated Summary:", summary);
    return { success: true, summary: summary };
};

export const logWater = async ({ amount }, getCommonHeaders) => {
    console.log(`💧 Tool called: log_water with amount: ${amount}`);
    addWaterEntry(amount, "ml", getCommonHeaders)
    return { success: true, message: `Successfully logged ${amount} of water.` };
};

export const logWeight = async ({ weight }, getCommonHeaders) => {
    console.log(`⚖️ Tool called: log_weight with weight: ${weight}kg`);
    addWeightEntry(weight, getCommonHeaders)
    return { success: true, message: `Successfully logged weight as ${weight} kg.` };
};

export const logSleep = async ({ duration }, getCommonHeaders) => {
    console.log(`😴 Tool called: log_sleep with duration: ${duration} minutes`);
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    addSleepEntry(duration, getCommonHeaders)
    return { success: true, message: `Successfully logged ${hours}h ${minutes}m of sleep.` };
};

export const logActivity = async ({ activity_name, duration, intensity }, getCommonHeaders) => {
    console.log(`🏃 Tool called: log_activity: ${activity_name}, ${duration} mins, ${intensity || 'N/A'} intensity`);
    addActivityEntry(activity_name, duration, intensity, getCommonHeaders)
    return { success: true, message: `Successfully logged ${duration} minutes of ${activity_name}.` };
};

export const logFood = async ({ food_name, meal_type, amount }, userDetails, getCommonHeaders) => {
    console.log(`🍲 Tool called: log_food: ${food_name}, Meal: ${meal_type}, Amount: ${amount || 'N/A'}`);
    addFoodEntry(food_name, meal_type, userDetails, getCommonHeaders)
    return { success: true, message: `Successfully logged ${food_name} for ${meal_type}.` };
};

export const scheduleCoachCall = async ({ date, time }, userDetails, apiKey, getCommonHeaders) => {
    console.log(`📅 Tool called: schedule_coach_call for ${date} at ${time}`);
    scheduleCoachAppointment(date, time, userDetails, apiKey, getCommonHeaders)
    return { success: true, message: `Your coach call has been scheduled for ${date} at ${time}.` };
};

export const scheduleDoctorCall = async ({ date, time }, getCommonHeaders) => {
    console.log(`🩺 Tool called: schedule_doctor_call for ${date} at ${time}`);
    scheduleDoctorAppointment(date, time, getCommonHeaders)
    return { success: true, message: `Your doctor's appointment is confirmed for ${date} at ${time}.` };
};

export const messageCoach = async ({ message }, getCommonHeaders) => {
    console.log(`💬 Tool called: message_coach with message: "${message}"`);
    sendUserConversationEncrypted(message, getCommonHeaders)
    return { success: true, message: `Your message has been sent to the coach.` };
};

export const logGlucose = async ({ level }, userDetails, apiKey, getCommonHeaders) => {
    addGlucoseEntry(level, userDetails, apiKey, getCommonHeaders)
    console.log(`🩸 Tool called: log_glucose with level: ${level}`);
    return { success: true, message: `Successfully logged glucose level of ${level} mmol/L.` };
};

export const getAvailableTools = (healthData, userDetails, apiKey, getCommonHeaders) => ({
    get_health_summary: (args) => getHealthSummary(args, healthData),
    log_water: (args) => logWater(args, getCommonHeaders),
    log_weight: (args) => logWeight(args, getCommonHeaders),
    log_sleep: (args) => logSleep(args, getCommonHeaders),
    log_activity: (args) => logActivity(args, getCommonHeaders),
    log_food: (args) => logFood(args, userDetails, getCommonHeaders),
    schedule_coach_call: (args) => scheduleCoachCall(args, userDetails, apiKey, getCommonHeaders),
    schedule_doctor_call: (args) => scheduleDoctorCall(args, getCommonHeaders),
    message_coach: (args) => messageCoach(args, getCommonHeaders),
    log_glucose: (args) => logGlucose(args, userDetails, apiKey, getCommonHeaders),
    fetch_my_orders: (args) => fetchMyOrders(args, getCommonHeaders),
});

export const toolDeclarations = {
    functionDeclarations: [
        {
            name: "get_health_summary",
            description: "Gets a structured summary of the user's logged health data for a specific day.",
            parameters: {
                type: "OBJECT",
                properties: { day: { type: "STRING", description: "The day to summarize: 'today' or 'yesterday'." } },
                required: ["day"],
            },
        },
        {
            name: "log_water",
            description: "Logs the amount of water the user has consumed.",
            parameters: {
                type: "OBJECT",
                properties: { amount: { type: "STRING", description: "The amount of water, e.g., '250', alway give entry in milliliters format, if the entries are in different unit convert it to ml" } },
                required: ["amount"],
            },
        },
        {
            name: "log_weight",
            description: "Logs the user's current weight.",
            parameters: {
                type: "OBJECT",
                properties: { weight: { type: "NUMBER", description: "Weight in kilograms." } },
                required: ["weight"],
            },
        },
        {
            name: "log_sleep",
            description: "Logs sleep duration.",
            parameters: {
                type: "OBJECT",
                properties: { duration: { type: "NUMBER", description: "Total sleep duration in minutes." } },
                required: ["duration"],
            },
        },
        {
            name: "log_activity",
            description: "Logs a physical activity.",
            parameters: {
                type: "OBJECT",
                properties: {
                    activity_name: { type: "STRING", description: "Name of the activity, e.g., 'Running'." },
                    duration: { type: "NUMBER", description: "Duration in minutes." },
                    intensity: { type: "STRING", description: "Intensity: 'easy', 'moderate', or 'hard'." },
                },
                required: ["activity_name", "duration"],
            },
        },
        {
            name: "log_food",
            description: "Logs a food item.",
            parameters: {
                type: "OBJECT",
                properties: {
                    food_name: { type: "STRING", description: "Name of the food, e.g., 'Chicken Salad'." },
                    meal_type: { type: "STRING", description: "e.g., 'Breakfast', 'Lunch', 'Dinner', 'Snack'." },
                    amount: { type: "STRING", description: "Portion size, e.g., '1 bowl'." },
                },
                required: ["food_name", "meal_type"],
            },
        },
        {
            name: "schedule_coach_call",
            description: "Schedules a coaching session.",
            parameters: {
                type: "OBJECT",
                properties: {
                    date: { type: "STRING", description: "Date in YYYY-MM-DD format." },
                    time: { type: "STRING", description: "Time in 24-hour HH:MM format." },
                },
                required: ["date", "time"],
            },
        },
        {
            name: "schedule_doctor_call",
            description: "Schedules a doctor's appointment or call, You dont need doctors name and contact information",
            parameters: {
                type: "OBJECT",
                properties: {
                    date: { type: "STRING", description: "Take users Date" },
                    time: { type: "STRING", description: "Take users Time" },
                },
                required: ["date", "time"],
            },
        },
        {
            name: "message_coach",
            description: "Sends a text message to the coach.",
            parameters: {
                type: "OBJECT",
                properties: { message: { type: "STRING", description: "The message content." } },
                required: ["message"],
            },
        },
        {
            name: "log_glucose",
            description: "Logs a blood glucose reading.",
            parameters: {
                type: "OBJECT",
                properties: { level: { type: "NUMBER", description: "The blood glucose level." } },
                required: ["level"],
            },
        },
    ]
};
